import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Megaphone, Plus, Loader2, Sparkles, Languages, Download, FileText, ImageIcon, Monitor, Smartphone, FileType2,
  Send, Save, ArrowLeft, Trash2, Copy, Archive, Eye, EyeOff, Pencil, Star, AlertTriangle, Users, CheckCircle2, Cake, CalendarDays, Trophy,
} from "lucide-react";
import RichTextEditor from "@/components/announcement/RichTextEditor";
import AnnouncementPamphlet from "@/components/announcement/AnnouncementPamphlet";
import ToppersGenerator from "@/components/announcement/ToppersGenerator";
import BirthdayGenerator from "@/components/announcement/BirthdayGenerator";
import TestTimetableGenerator from "@/components/announcement/TestTimetableGenerator";
import CompetitionGenerator from "@/components/announcement/CompetitionGenerator";
import { TEMPLATES, greetingFor } from "@/components/announcement/templates";
import { exportImage, exportPdf } from "@/components/announcement/exportUtils";

const LANGS = [{ v: "en", l: "English" }, { v: "kn", l: "ಕನ್ನಡ (Kannada)" }, { v: "hi", l: "हिन्दी (Hindi)" }];
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  published: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  archived: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

function emptyNotice() {
  return {
    title: "", category: "General", description: "", language: "en",
    announcement_date: new Date().toISOString().slice(0, 10),
    event_date: "", event_time: "", venue: "",
    template_id: "formal", accent_color: "", priority: "normal", pinned: false, signoff: "",
    show_salutation: true, salutation: "", regards_text: "Regards,", signoff_org: "",
    audience: { type: "school", class_names: [], sections: [], student_ids: [], recipients: ["students"] },
  };
}

export default function Announcements({ embedded = false }) {
  const { user } = useAuth();
  const [view, setView] = useState("list"); // list | editor | toppers
  const [editing, setEditing] = useState(null);
  const inner = (
    view === "editor"
      ? <Editor initial={editing} user={user} onBack={() => { setView("list"); setEditing(null); }} />
      : view === "toppers"
        ? <ToppersGenerator onBack={() => setView("list")} />
        : view === "birthday"
          ? <BirthdayGenerator onBack={() => setView("list")} />
          : view === "test-timetable"
            ? <TestTimetableGenerator onBack={() => setView("list")} />
            : view === "competition"
              ? <CompetitionGenerator onBack={() => setView("list")} />
              : <ManagementList user={user} onNew={() => { setEditing(null); setView("editor"); }} onEdit={(n) => { setEditing(n); setView("editor"); }} onToppers={() => setView("toppers")} onBirthday={() => setView("birthday")} onTestTimetable={() => setView("test-timetable")} onCompetition={() => setView("competition")} />
  );
  if (embedded) return inner;
  return <Layout>{inner}</Layout>;
}

// ─────────────────────────── Management list ───────────────────────────
function ManagementList({ user, onNew, onEdit, onToppers, onBirthday, onTestTimetable, onCompetition }) {
  const [notices, setNotices] = useState(null);
  const [filter, setFilter] = useState("all");
  const [bdayCount, setBdayCount] = useState(0);
  const readonly = user?.role === "super_admin";

  const load = async () => {
    try {
      const { data } = await api.get("/notices", { params: filter === "all" ? {} : { status: filter } });
      setNotices(data.notices);
    } catch { toast.error("Could not load announcements"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    api.get("/notices/birthdays/count").then(({ data }) => setBdayCount(data.count || 0)).catch(() => {});
  }, []);

  const act = async (id, action) => {
    try {
      if (action === "delete") { await api.delete(`/notices/${id}`); toast.success("Deleted"); }
      else { await api.post(`/notices/${id}/${action}`); toast.success("Done"); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="space-y-6" data-testid="announcements-list">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300 flex items-center justify-center"><Megaphone className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Announcements</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Create branded notices & pamphlets and share them with your school.</p>
          </div>
        </div>
        {!readonly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-full relative" onClick={onBirthday} data-testid="birthday-btn">
              <Cake className="h-4 w-4 mr-1 text-pink-500" /> Birthday
              {bdayCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pink-500 text-white text-[11px] font-bold" data-testid="birthday-badge">{bdayCount}</span>
              )}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={onTestTimetable} data-testid="test-timetable-btn"><CalendarDays className="h-4 w-4 mr-1 text-violet-500" /> Test Timetable</Button>
            <Button variant="outline" className="rounded-full" onClick={onCompetition} data-testid="competition-btn"><Trophy className="h-4 w-4 mr-1 text-amber-500" /> Competition</Button>
            <Button variant="outline" className="rounded-full" onClick={onToppers} data-testid="class-toppers-btn"><Star className="h-4 w-4 mr-1" /> Class Toppers</Button>
            <Button className="rounded-full" onClick={onNew} data-testid="new-announcement-btn"><Plus className="h-4 w-4 mr-1" /> Create Announcement</Button>
          </div>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-slate-100 dark:bg-slate-800/60">
          {["all", "draft", "published", "archived"].map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`filter-${s}`} className="capitalize">{s}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {notices === null ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : notices.length === 0 ? (
        <div className="py-16 text-center rounded-2xl border border-dashed border-slate-200 dark:border-slate-700" data-testid="announcements-empty">
          <Megaphone className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="text-slate-900 dark:text-slate-100 font-medium">No announcements yet</div>
          <div className="text-slate-500 text-sm mt-1">Create your first branded announcement.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {notices.map((n) => (
            <div key={n.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`notice-row-${n.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {n.priority === "important" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500 text-white">IMPORTANT</span>}
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[n.status] || ""}`}>{n.status}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300">{n.category}</span>
                </div>
                <div className="font-semibold text-slate-900 dark:text-slate-100 mt-1.5 truncate">{n.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {n.audience_label}</span>
                  <span>By {n.created_by?.name}</span>
                  {n.status === "published" && <span>· {n.views || 0} views · {n.downloads || 0} downloads</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <IconBtn title="Edit / Preview" onClick={() => onEdit(n)} testid={`edit-${n.id}`}><Pencil className="h-4 w-4" /></IconBtn>
                {user?.role !== "super_admin" && <>
                  {n.status !== "published"
                    ? <IconBtn title="Publish" onClick={() => act(n.id, "publish")} testid={`publish-${n.id}`}><Send className="h-4 w-4 text-emerald-600" /></IconBtn>
                    : <IconBtn title="Unpublish" onClick={() => act(n.id, "unpublish")} testid={`unpublish-${n.id}`}><EyeOff className="h-4 w-4" /></IconBtn>}
                  <IconBtn title="Duplicate" onClick={() => act(n.id, "duplicate")} testid={`duplicate-${n.id}`}><Copy className="h-4 w-4" /></IconBtn>
                  {n.status !== "archived" && <IconBtn title="Archive" onClick={() => act(n.id, "archive")} testid={`archive-${n.id}`}><Archive className="h-4 w-4" /></IconBtn>}
                  <IconBtn title="Delete" onClick={() => { if (window.confirm("Delete this announcement?")) act(n.id, "delete"); }} testid={`delete-${n.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></IconBtn>
                </>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const IconBtn = ({ onClick, title, children, testid }) => (
  <button type="button" title={title} data-testid={testid} onClick={onClick} className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">{children}</button>
);

// ─────────────────────────── Editor ───────────────────────────
function Editor({ initial, user, onBack }) {
  const navigate = useNavigate();
  const [n, setN] = useState(() => initial ? { ...emptyNotice(), ...initial, audience: { ...emptyNotice().audience, ...(initial.audience || {}) } } : emptyNotice());
  const [meta, setMeta] = useState(null);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiImgOpen, setAiImgOpen] = useState(false);
  const [imgGenerating, setImgGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pamphletRef = useRef(null);
  const imgInputRef = useRef(null);
  const [imgUploading, setImgUploading] = useState(false);
  const readonly = user?.role === "super_admin";

  useEffect(() => {
    api.get("/notices/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/school/profile").then((r) => setProfile(r.data.profile)).catch(() => {});
  }, []);

  const set = (k, v) => setN((p) => ({ ...p, [k]: v }));
  const setAud = (k, v) => setN((p) => ({ ...p, audience: { ...p.audience, [k]: v } }));
  const brandForPreview = useMemo(() => (n.status === "published" && n.brand_snapshot) ? n.brand_snapshot : (profile || {}), [n, profile]);
  const previewData = useMemo(() => ({ ...n, accent_color: n.accent_color || undefined }), [n]);

  const persist = async ({ publish } = {}) => {
    if (!n.title.trim()) { toast.error("Enter an announcement title"); return null; }
    setSaving(true);
    try {
      let res;
      if (n.id) {
        await api.put(`/notices/${n.id}`, n);
        res = { ...n };
        if (publish) { const { data } = await api.post(`/notices/${n.id}/publish`); res = data.notice; }
      } else {
        const { data } = await api.post("/notices", n, { params: publish ? { publish: true } : {} });
        res = data.notice;
      }
      setN((p) => ({ ...p, ...res }));
      toast.success(publish ? "Published" : "Saved");
      return res;
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not save";
      toast.error(msg, msg.includes("profile information is incomplete") ? {
        action: user?.role === "teacher" ? undefined : { label: "Update Profile", onClick: () => navigate("/admin/settings?tab=school-profile") },
      } : {});
      return null;
    } finally { setSaving(false); }
  };

  const runAI = async (prompt, language) => {
    setBusy(true);
    try {
      const { data } = await api.post("/notices/ai/generate", { prompt, category: n.category, language: language || n.language });
      setN((p) => ({
        ...p,
        language: language || p.language,
        title: data.title || p.title,
        description: data.body_html || p.description,
        event_date: data.event_date || p.event_date,
        event_time: data.event_time || p.event_time,
        venue: data.venue || p.venue,
        signoff: data.signoff || p.signoff,
      }));
      toast.success("Draft generated — edit anything you like");
      setAiOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "AI generation failed"); }
    finally { setBusy(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/notices/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.image);
      toast.success("Image added");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add image"); }
    finally { setImgUploading(false); if (imgInputRef.current) imgInputRef.current.value = ""; }
  };

  const runAIImage = async (prompt) => {
    setImgGenerating(true);
    try {
      const { data } = await api.post("/notices/ai/image", { prompt, title: n.title, category: n.category });
      set("image", data.image);
      toast.success("Illustration added");
      setAiImgOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not generate image"); }
    finally { setImgGenerating(false); }
  };

  const translate = async (target) => {
    if (!n.description) { toast.error("Write some content first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/notices/ai/translate", { text: n.description, target_language: target });
      setN((p) => ({ ...p, description: data.text, language: target }));
      toast.success("Translated");
    } catch (e) { toast.error(e.response?.data?.detail || "Translation failed"); }
    finally { setBusy(false); }
  };

  const doExport = async (kind) => {
    if (!profile?.complete) {
      toast.error("School profile is incomplete. Complete the Logo, Name, Address and Phone before downloading.", {
        action: user?.role === "teacher" ? undefined : { label: "Update Profile", onClick: () => navigate("/admin/settings?tab=school-profile") },
      });
      return;
    }
    if (!pamphletRef.current) return;
    setExporting(true);
    try {
      if (kind === "pdf") await exportPdf(pamphletRef.current, n.title, "portrait");
      else await exportImage(pamphletRef.current, n.title, kind);
      if (n.id) api.post(`/notices/${n.id}/download`).catch(() => {});
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const deviceWidth = device === "mobile" ? 380 : 760;

  return (
    <div className="space-y-5" data-testid="announcement-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" className="rounded-full" onClick={onBack} data-testid="back-to-list"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        {!readonly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => persist({})} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save draft
            </Button>
            <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => persist({ publish: true })} disabled={saving} data-testid="publish-btn">
              <Send className="h-4 w-4 mr-1" /> Publish
            </Button>
          </div>
        )}
      </div>

      {!profile?.complete && (
        <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" data-testid="profile-incomplete-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>School profile is incomplete — publishing & downloads are blocked until the Logo, Name, Address and Phone are set.</span>
          {user?.role !== "teacher" && <button className="underline font-medium ml-1" onClick={() => navigate("/admin/settings?tab=school-profile")} data-testid="goto-profile-btn">Update School Profile</button>}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Title <span className="text-rose-500">*</span></Label>
              <Input className="mt-1 rounded-lg" value={n.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Parent-Teacher Meeting" data-testid="notice-title" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={n.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="notice-category"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.categories || ["General"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Announcement date</Label>
              <Input type="date" className="mt-1 rounded-lg" value={n.announcement_date} onChange={(e) => set("announcement_date", e.target.value)} data-testid="notice-date" />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setAiOpen(true)} disabled={busy} data-testid="ai-generate-btn"><Sparkles className="h-4 w-4 mr-1 text-violet-500" /> Generate with AI</Button>
            <input ref={imgInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadImage} data-testid="notice-image-input" />
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => imgInputRef.current?.click()} disabled={imgUploading} data-testid="add-image-btn">
              {imgUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ImageIcon className="h-4 w-4 mr-1 text-sky-500" />} Add Image
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setAiImgOpen(true)} disabled={imgGenerating} data-testid="ai-image-btn">
              {imgGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1 text-fuchsia-500" />} AI Image
            </Button>
            <div className="flex items-center gap-1">
              <Languages className="h-4 w-4 text-slate-400" />
              <Select onValueChange={translate}>
                <SelectTrigger className="h-8 rounded-full w-[150px] text-xs" data-testid="translate-select"><SelectValue placeholder="Translate to…" /></SelectTrigger>
                <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>

          {n.image && (
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-2" data-testid="notice-image-thumb">
              <img src={n.image} alt="Featured" className="h-14 w-20 object-cover rounded-lg" />
              <span className="text-xs text-slate-500 flex-1">Featured image added to the pamphlet.</span>
              <button type="button" className="h-8 w-8 flex items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30" onClick={() => set("image", "")} data-testid="remove-image-btn"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}

          <div>
            <Label className="mb-1 block">Description <span className="text-rose-500">*</span></Label>
            <RichTextEditor value={n.description} onChange={(v) => set("description", v)} />
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Event date <span className="text-xs text-slate-400">(opt)</span></Label><Input className="mt-1 rounded-lg" value={n.event_date} onChange={(e) => set("event_date", e.target.value)} placeholder="12 Aug 2026" data-testid="notice-event-date" /></div>
            <div><Label>Event time <span className="text-xs text-slate-400">(opt)</span></Label><Input className="mt-1 rounded-lg" value={n.event_time} onChange={(e) => set("event_time", e.target.value)} placeholder="10:00 AM" data-testid="notice-event-time" /></div>
            <div><Label>Venue <span className="text-xs text-slate-400">(opt)</span></Label><Input className="mt-1 rounded-lg" value={n.venue} onChange={(e) => set("venue", e.target.value)} placeholder="School ground" data-testid="notice-venue" /></div>
          </div>

          {/* Salutation & sign-off */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3" data-testid="salutation-signoff-box">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Salutation & Sign-off</div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <Switch checked={n.show_salutation !== false} onCheckedChange={(v) => set("show_salutation", v)} data-testid="notice-show-salutation" />
                Show &ldquo;Dear …&rdquo; line
              </label>
              {n.show_salutation !== false && (
                <div className="flex-1 min-w-52">
                  <Input className="rounded-lg" value={n.salutation || ""} onChange={(e) => set("salutation", e.target.value)}
                    placeholder={`Auto: ${greetingFor(n.audience?.recipients || ["students"])}`} data-testid="notice-salutation" />
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Closing line <span className="text-xs text-slate-400">(clear to hide)</span></Label>
                <Input className="mt-1 rounded-lg" value={n.regards_text ?? "Regards,"} onChange={(e) => set("regards_text", e.target.value)} placeholder="Regards," data-testid="notice-regards" />
              </div>
              <div>
                <Label>Sign-off name <span className="text-xs text-slate-400">(opt)</span></Label>
                <Input className="mt-1 rounded-lg" value={n.signoff} onChange={(e) => set("signoff", e.target.value)} placeholder="Principal" data-testid="notice-signoff" />
              </div>
              <div>
                <Label>Below-name line <span className="text-xs text-slate-400">(opt)</span></Label>
                <Input className="mt-1 rounded-lg" value={n.signoff_org || ""} onChange={(e) => set("signoff_org", e.target.value)} placeholder={profile?.name || "School name"} data-testid="notice-signoff-org" />
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Language</Label>
              <Select value={n.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="notice-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"><Switch checked={n.priority === "important"} onCheckedChange={(v) => set("priority", v ? "important" : "normal")} data-testid="notice-important" /> <Star className="h-4 w-4" /> Mark as Important</label>
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"><Switch checked={!!n.pinned} onCheckedChange={(v) => set("pinned", v)} data-testid="notice-pinned" /> Pin to top</label>
          </div>

          {/* Template + accent */}
          <div>
            <Label className="mb-2 block">Design template</Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="template-picker">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" onClick={() => set("template_id", t.id)} data-testid={`template-${t.id}`}
                  className={`rounded-xl border-2 p-2 text-left transition-all ${n.template_id === t.id ? "border-sky-500 ring-2 ring-sky-200 dark:ring-sky-800" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}>
                  <div className="h-8 rounded-md mb-1.5" style={{ background: t.headerBg, borderBottom: `3px solid ${t.accent}` }} />
                  <div className="text-[11px] font-medium text-slate-700 dark:text-slate-200 truncate">{t.name}</div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Label className="mb-0 text-xs">Accent override</Label>
              <input type="color" value={n.accent_color || TEMPLATES.find((t) => t.id === n.template_id)?.accent || "#0284c7"} onChange={(e) => set("accent_color", e.target.value)} className="w-8 h-8 rounded-lg border cursor-pointer" data-testid="accent-override" />
              {n.accent_color && <button className="text-xs text-slate-400 underline" onClick={() => set("accent_color", "")}>reset</button>}
            </div>
          </div>

          {/* Audience */}
          <AudienceSelector meta={meta} audience={n.audience} setAud={setAud} />
        </div>

        {/* Preview */}
        <div className="lg:sticky lg:top-20 self-start space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full p-1">
              <DeviceBtn active={device === "desktop"} onClick={() => setDevice("desktop")} testid="device-desktop"><Monitor className="h-4 w-4" /></DeviceBtn>
              <DeviceBtn active={device === "mobile"} onClick={() => setDevice("mobile")} testid="device-mobile"><Smartphone className="h-4 w-4" /></DeviceBtn>
              <DeviceBtn active={device === "a4"} onClick={() => setDevice("a4")} testid="device-a4"><FileType2 className="h-4 w-4" /></DeviceBtn>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("pdf")} disabled={exporting} data-testid="export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("png")} disabled={exporting} data-testid="export-png"><ImageIcon className="h-4 w-4 mr-1" /> PNG</Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("jpeg")} disabled={exporting} data-testid="export-jpg"><Download className="h-4 w-4 mr-1" /> JPG</Button>
              {exporting && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            </div>
          </div>
          <div className={`rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 p-4 overflow-auto ${device === "a4" ? "max-h-[80vh]" : ""}`} data-testid="preview-surface">
            <div className="mx-auto shadow-xl" style={{ width: deviceWidth, maxWidth: "100%" }}>
              <AnnouncementPamphlet ref={pamphletRef} data={previewData} profile={brandForPreview} />
            </div>
          </div>
        </div>
      </div>

      <AIDialog open={aiOpen} onOpenChange={setAiOpen} onGenerate={runAI} busy={busy} defaultLang={n.language} />
      <AIImageDialog open={aiImgOpen} onOpenChange={setAiImgOpen} onGenerate={runAIImage} busy={imgGenerating} defaultPrompt={n.title} category={n.category} />
    </div>
  );
}

function DeviceBtn({ active, onClick, children, testid }) {
  return <button type="button" onClick={onClick} data-testid={testid} className={`h-8 w-9 flex items-center justify-center rounded-full transition-colors ${active ? "bg-white dark:bg-slate-700 text-sky-600 shadow-sm" : "text-slate-400"}`}>{children}</button>;
}

// ─────────────────────────── Audience selector ───────────────────────────
function AudienceSelector({ meta, audience, setAud }) {
  const canSchool = meta?.can_school_wide;
  const sections = meta?.sections || [];
  const classes = meta?.classes || [];

  const options = canSchool
    ? [["school", "Entire school"], ["all_students", "All students"], ["all_teachers", "All teachers"], ["classes", "Specific class(es)"], ["sections", "Specific section(s)"], ["students", "Selected students"]]
    : [["classes", "My class(es)"], ["sections", "My section(s)"], ["students", "Selected students"]];

  const toggleClass = (cn) => {
    const has = audience.class_names.includes(cn);
    setAud("class_names", has ? audience.class_names.filter((c) => c !== cn) : [...audience.class_names, cn]);
  };
  const toggleSection = (s) => {
    const key = `${s.class_name}-${s.section}`;
    const has = audience.sections.some((x) => `${x.class_name}-${x.section}` === key);
    setAud("sections", has ? audience.sections.filter((x) => `${x.class_name}-${x.section}` !== key) : [...audience.sections, s]);
  };
  const toggleRecipient = (r) => {
    const has = (audience.recipients || []).includes(r);
    setAud("recipients", has ? audience.recipients.filter((x) => x !== r) : [...(audience.recipients || []), r]);
  };

  useEffect(() => { if (!canSchool && audience.type === "school") setAud("type", "classes"); /* eslint-disable-next-line */ }, [canSchool]);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-3 bg-white dark:bg-slate-900" data-testid="audience-selector">
      <Label className="flex items-center gap-2"><Users className="h-4 w-4" /> Target audience</Label>
      <Select value={audience.type} onValueChange={(v) => setAud("type", v)}>
        <SelectTrigger className="rounded-lg" data-testid="audience-type"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>

      {audience.type === "classes" && (
        <div className="flex flex-wrap gap-1.5" data-testid="class-chips">
          {classes.map((cn) => (
            <Chip key={cn} active={audience.class_names.includes(cn)} onClick={() => toggleClass(cn)} testid={`class-chip-${cn}`}>Class {cn}</Chip>
          ))}
        </div>
      )}
      {audience.type === "sections" && (
        <div className="flex flex-wrap gap-1.5" data-testid="section-chips">
          {sections.map((s) => {
            const key = `${s.class_name}-${s.section}`;
            return <Chip key={key} active={audience.sections.some((x) => `${x.class_name}-${x.section}` === key)} onClick={() => toggleSection(s)} testid={`section-chip-${key}`}>{s.class_name}-{s.section}</Chip>;
          })}
        </div>
      )}
      {audience.type === "students" && (
        <StudentPicker sections={sections} selected={audience.student_ids} onChange={(ids) => setAud("student_ids", ids)} />
      )}

      {audience.type !== "all_teachers" && audience.type !== "all_staff" && (
        <div>
          <div className="text-xs text-slate-500 mb-1.5">Send to</div>
          <div className="flex gap-1.5">
            {["students", "parents", "teachers"].map((r) => (
              <Chip key={r} active={(audience.recipients || []).includes(r)} onClick={() => toggleRecipient(r)} testid={`recipient-${r}`}>{r === "students" ? "Students" : r === "parents" ? "Parents" : "Teachers"}</Chip>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">In-app feed reaches students & teachers. Parent reach is via their registered contact.</p>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? "bg-sky-500 text-white border-sky-500" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-sky-300"}`}>
      {children}{active ? " ✓" : ""}
    </button>
  );
}

function StudentPicker({ sections, selected, onChange }) {
  const [sel, setSel] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadStudents = async (key) => {
    if (!key) return;
    const [class_name, section] = key.split("|");
    setLoading(true);
    try {
      const { data } = await api.get("/students/list", { params: { class_name, section } });
      setStudents(data.students || data || []);
    } catch { setStudents([]); }
    finally { setLoading(false); }
  };

  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="space-y-2">
      <Select value={sel} onValueChange={(v) => { setSel(v); loadStudents(v); }}>
        <SelectTrigger className="rounded-lg" data-testid="student-picker-section"><SelectValue placeholder="Pick a class-section" /></SelectTrigger>
        <SelectContent>{sections.map((s) => <SelectItem key={`${s.class_name}|${s.section}`} value={`${s.class_name}|${s.section}`}>{s.class_name}-{s.section}</SelectItem>)}</SelectContent>
      </Select>
      {loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
        <div className="max-h-48 overflow-y-auto grid gap-1" data-testid="student-picker-list">
          {students.map((st) => (
            <label key={st.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
              <input type="checkbox" checked={selected.includes(st.id)} onChange={() => toggle(st.id)} data-testid={`student-cb-${st.id}`} />
              <span className="text-slate-700 dark:text-slate-200">{st.name}</span>
              <span className="text-xs text-slate-400 ml-auto">Roll {st.roll_number}</span>
            </label>
          ))}
        </div>
      )}
      {selected.length > 0 && <div className="text-xs text-sky-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {selected.length} selected</div>}
    </div>
  );
}

// ─────────────────────────── AI dialog ───────────────────────────
function AIDialog({ open, onOpenChange, onGenerate, busy, defaultLang = "en" }) {
  const [prompt, setPrompt] = useState("");
  const [lang, setLang] = useState(defaultLang);
  useEffect(() => { setLang(defaultLang); }, [defaultLang, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-500" /> Generate with AI</DialogTitle>
          <DialogDescription>Describe the announcement in a sentence or two. AI drafts a professional version you can edit.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-prompt"
          placeholder="e.g. Independence Day celebration on 15 August at 9 AM in the school ground. Students should wear traditional dress." />
        <div className="flex items-center gap-2">
          <Languages className="h-4 w-4 text-slate-400" />
          <span className="text-sm text-slate-500">Write in</span>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-9 rounded-lg w-[170px]" data-testid="ai-language"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full" onClick={() => onGenerate(prompt, lang)} disabled={busy || prompt.trim().length < 3} data-testid="ai-generate-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />} Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AIImageDialog({ open, onOpenChange, onGenerate, busy, defaultPrompt = "", category = "General" }) {
  const [prompt, setPrompt] = useState("");
  useEffect(() => { if (open) setPrompt(defaultPrompt); }, [open, defaultPrompt]);
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="rounded-2xl" data-testid="ai-image-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-fuchsia-500" /> Generate an illustration</DialogTitle>
          <DialogDescription>AI creates a festival/event illustration (no text) that matches your announcement. It's added as the pamphlet image.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-image-prompt"
          placeholder="e.g. Independence Day with Indian flag and happy children" />
        <div className="text-xs text-slate-400">Category: {category}. Generation takes ~10–20 seconds — please wait.</div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="rounded-full" onClick={() => onGenerate(prompt)} disabled={busy || prompt.trim().length < 3} data-testid="ai-image-submit">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-1" /> Generate</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
